import React, { useState, useEffect } from 'react';
import './MobilePDFViewer.css';

interface MobilePDFViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
  documentTitle?: string;
}

// Declare PDF.js types
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

const MobilePDFViewer: React.FC<MobilePDFViewerProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  documentTitle
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [loading, setLoading] = useState(true);

  // Load PDF.js and detect page count
  useEffect(() => {
    if (!pdfUrl || !isOpen) return;

    const loadPdfJs = async () => {
      try {
        // Load PDF.js from CDN if not already loaded
        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });

          // Set worker
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        // Load the PDF to get page count
        const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        
        console.log('📄 PDF loaded:', pdf.numPages, 'pages');
        setTotalPages(pdf.numPages);
        setLoading(false);
      } catch (error) {
        console.error('❌ Error loading PDF:', error);
        setLoading(false);
        // Keep default totalPages = 1 on error
      }
    };

    setLoading(true);
    loadPdfJs();
  }, [pdfUrl, isOpen]);

  if (!isOpen) return null;

  const handlePageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoom = (newZoom: number) => {
    setZoom(Math.max(50, Math.min(200, newZoom)));
  };

  return (
    <div className="pdf-modal-overlay">
      <div className="pdf-modal">
        {/* Header */}
        <div className="pdf-header">
          <button className="back-btn" onClick={onClose}>
            ← Back
          </button>
          <h3 className="pdf-title">{documentTitle || 'PDF Document'}</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* PDF Controls */}
        <div className="pdf-controls">
          <div className="page-info">
            {loading ? (
              <span>Loading PDF...</span>
            ) : (
              <span>Page {currentPage} of {totalPages}</span>
            )}
          </div>
          <div className="zoom-controls">
            <button 
              className="zoom-btn" 
              onClick={() => handleZoom(zoom - 25)}
              disabled={zoom <= 50}
            >
              -
            </button>
            <span className="zoom-level">{zoom}%</span>
            <button 
              className="zoom-btn" 
              onClick={() => handleZoom(zoom + 25)}
              disabled={zoom >= 200}
            >
              +
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="pdf-content">
          {pdfUrl ? (
            <iframe
              key={`pdf-${currentPage}-${zoom}`}
              src={`${pdfUrl}#page=${currentPage}&zoom=${zoom}`}
              className="pdf-iframe"
              title="PDF Document"
            />
          ) : (
            <div className="pdf-placeholder">
              <div className="placeholder-icon">📄</div>
              <p>PDF document will be displayed here</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="pdf-navigation">
          <button 
            className="nav-btn" 
            onClick={() => handlePageChange('prev')}
            disabled={currentPage <= 1}
          >
            ←
          </button>
          
          <div className="page-thumbnails">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`page-thumb ${pageNum === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button 
            className="nav-btn" 
            onClick={() => handlePageChange('next')}
            disabled={currentPage >= totalPages}
          >
            →
          </button>
        </div>

        {/* Touch Gestures Hint */}
        <div className="gesture-hint">
          <span>👆 Tap to zoom • 👈👈 Swipe to navigate</span>
        </div>
      </div>
    </div>
  );
};

export default MobilePDFViewer; 